import { createFileRoute, Link } from '@tanstack/react-router'
import { useRef, useState } from 'react'
import {
  ArrowLeft,
  ArrowRight,
  FileSearch,
  FileText,
  FileType2,
  Search,
  X,
} from 'lucide-react'
import { useDocuments } from '@/api/queries'
import {
  isProcessing,
  MIME_DOCX,
  progressStageLabel,
  type DocumentStatus,
  type DocumentSummary,
} from '@/api/docfiller-types'
import ErrorFallback from '@/components/ErrorFallback'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
} from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Kbd } from '@/components/ui/kbd'
import { InlineError } from '@/components/ui/loading-state'
import { PageHeader } from '@/components/ui/page-header'
import { Skeleton } from '@/components/ui/skeleton'
import { useKeyboardShortcut } from '@/hooks/useKeyboardShortcut'
import { formatLocalDateTime } from '@/lib/format-date'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/docfiller/chats')({
  component: DocfillerDocumentSearchPage,
  errorComponent: ErrorFallback,
})

const STATUS_TONE: Record<DocumentStatus, React.ComponentProps<typeof Badge>['tone']> = {
  uploaded: 'info',
  parsing: 'info',
  parsed: 'success',
  filling: 'info',
  filled: 'success',
  error: 'danger',
}

export function DocfillerDocumentSearchPage() {
  const [query, setQuery] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const trimmedQuery = query.trim()
  const documents = useDocuments({ search: trimmedQuery || undefined })

  useKeyboardShortcut({ key: '/' }, () => {
    searchRef.current?.focus()
    searchRef.current?.select()
  })

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-4 py-8 md:px-8">
      <div className="space-y-3">
        <Link
          to="/docfiller"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-3" />
          Doc Filler
        </Link>
        <PageHeader
          eyebrow="Search"
          title="Document search"
          description="Find documents by filename, status, saved context, extracted field names and values, and document-scoped extracted text."
        />
      </div>

      <div className="relative max-w-2xl">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          ref={searchRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search filenames, fields, values, context, extracted text..."
          className="h-11 pl-9 pr-20"
          data-testid="document-search-input"
          aria-label="Search documents"
          aria-keyshortcuts="/"
        />
        {query ? (
          <button
            type="button"
            onClick={() => setQuery('')}
            className="absolute right-11 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Clear document search"
            data-testid="document-search-clear"
          >
            <X className="size-3.5" />
          </button>
        ) : null}
        <Kbd
          aria-hidden
          className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2"
        >
          /
        </Kbd>
      </div>

      {documents.isLoading ? (
        <ul className="space-y-2" aria-busy="true" aria-label="Loading documents">
          {Array.from({ length: 4 }).map((_, i) => (
            <li key={i}>
              <Card>
                <CardContent className="flex items-center gap-3 p-4">
                  <Skeleton className="size-9 rounded-md" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-4 w-56" />
                    <Skeleton className="h-3 w-72" />
                  </div>
                  <Skeleton className="h-8 w-20 rounded-md" />
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      ) : documents.error ? (
        <InlineError
          title="Could not search documents"
          message={
            documents.error instanceof Error
              ? documents.error.message
              : 'Check your connection and try again.'
          }
        />
      ) : documents.data && documents.data.length === 0 ? (
        <EmptyState
          icon={FileSearch}
          title={trimmedQuery ? 'No documents found' : 'No documents yet'}
          description={
            trimmedQuery
              ? 'Try a filename, field label, filled value, or text from an extracted context file.'
              : 'Upload documents before searching your workspace.'
          }
          action={
            trimmedQuery ? (
              <Button variant="outline" size="sm" onClick={() => setQuery('')}>
                Clear search
              </Button>
            ) : (
              <Button asChild size="sm">
                <Link to="/docfiller">Upload a document</Link>
              </Button>
            )
          }
          data-testid="document-search-empty"
        />
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground" data-testid="document-search-count">
            {documents.data?.length ?? 0} document
            {(documents.data?.length ?? 0) === 1 ? '' : 's'}
            {trimmedQuery ? ` matching "${trimmedQuery}"` : ' available'}
          </p>
          <ul className="space-y-2" data-testid="document-search-list">
            {(documents.data ?? []).map((doc) => (
              <li key={doc.uuid}>
                <DocumentSearchRow doc={doc} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function DocumentSearchRow({ doc }: { doc: DocumentSummary }) {
  const Icon = doc.mime_type === MIME_DOCX ? FileType2 : FileText
  const processing = isProcessing(doc.status)
  const statusLabel = processing
    ? progressStageLabel(doc.progress_stage)
    : doc.status === 'error' && doc.friendly_error
      ? doc.friendly_error
      : doc.status
  const matches = doc.search_matches ?? []

  return (
    <Card
      interactive
      className="aurora-card-hover"
      data-testid="document-search-row"
    >
      <CardContent className="flex items-start gap-3 p-4">
        <span
          aria-hidden
          className={cn(
            'mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-md',
            processing
              ? 'bg-primary-soft text-primary-soft-foreground'
              : 'bg-muted text-muted-foreground',
          )}
        >
          <Icon className="size-4" />
        </span>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Link
              to="/docfiller/$documentId"
              params={{ documentId: doc.uuid }}
              className="min-w-0 truncate text-sm font-medium text-foreground hover:underline"
            >
              {doc.source_filename}
            </Link>
            <Badge tone={STATUS_TONE[doc.status]} size="xs">
              {statusLabel}
            </Badge>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span>{formatLocalDateTime(doc.updated_at)}</span>
            <span>{Math.ceil(doc.size_bytes / 1024).toLocaleString()} KB</span>
          </div>
          {matches.length > 0 ? (
            <div className="grid gap-1.5" data-testid="document-search-matches">
              {matches.slice(0, 4).map((match, index) => (
                <div
                  key={`${match.source}-${match.label}-${index}`}
                  className="rounded-md border border-border/70 bg-muted/30 px-2.5 py-1.5 text-xs"
                >
                  <div className="font-medium text-foreground">{match.label}</div>
                  <div className="mt-0.5 text-muted-foreground">
                    {match.snippet}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              Filename and document metadata are searchable.
            </p>
          )}
        </div>
        <Button asChild size="sm" variant="outline" className="shrink-0">
          <Link to="/docfiller/$documentId" params={{ documentId: doc.uuid }}>
            Open
            <ArrowRight className="size-3" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  )
}
